// CANONICAL COPY (GH #7115). src/constants/frameworks/gdpr.js re-exports this file so the browser and the
// Function App share one compliance engine — one definition of "compliant". Edit here, never there.
'use strict';

// EU General Data Protection Regulation (GDPR) 2016/679 — framework mapping to TCF controls.
// GDPR article IDs follow the format GDPR-ART{article} or GDPR-ART{article}-{sub}.
//
// Multiple mappings with the same externalId are intentional — the last entry
// per externalId is used for the scfLookup (cross-reference display), while
// all entries contribute to controlToFrameworks and externalRefs.

const FRAMEWORK_GDPR = {
  id: 'fw-gdpr',
  type: 'law',
  name: 'General Data Protection Regulation',
  shortName: 'GDPR',
  abbrev: 'GDPR',
  description:
    'EU General Data Protection Regulation — comprehensive data protection and privacy framework applying to all organisations processing personal data of EU/EEA residents.',
  color: '#e879f9',
  version: '2016/679',
  jurisdiction: 'EU',
  sectionKeyDepth: 1,
  // One section per mapped article, named with the article's own title from
  // Regulation (EU) 2016/679. The mappings below band several articles under a
  // shared comment (13-14, 15-22, 44-49); those bands are kept as separate
  // sections here so each heading names the article it actually covers.
  sections: [
    { key: 'ART5', name: 'Art. 5 — Principles relating to processing of personal data' },
    { key: 'ART6', name: 'Art. 6 — Lawfulness of processing' },
    { key: 'ART7', name: 'Art. 7 — Conditions for consent' },
    { key: 'ART9', name: 'Art. 9 — Processing of special categories of personal data' },
    { key: 'ART13', name: 'Art. 13 — Information where data are collected from the data subject' },
    {
      key: 'ART14',
      name: 'Art. 14 — Information where data have not been obtained from the data subject',
    },
    { key: 'ART15', name: 'Art. 15 — Right of access by the data subject' },
    { key: 'ART16', name: 'Art. 16 — Right to rectification' },
    { key: 'ART17', name: 'Art. 17 — Right to erasure (right to be forgotten)' },
    { key: 'ART18', name: 'Art. 18 — Right to restriction of processing' },
    {
      key: 'ART19',
      name: 'Art. 19 — Notification obligation regarding rectification or erasure',
    },
    { key: 'ART20', name: 'Art. 20 — Right to data portability' },
    { key: 'ART21', name: 'Art. 21 — Right to object' },
    { key: 'ART22', name: 'Art. 22 — Automated individual decision-making, including profiling' },
    { key: 'ART24', name: 'Art. 24 — Responsibility of the controller' },
    { key: 'ART25', name: 'Art. 25 — Data protection by design and by default' },
    { key: 'ART28', name: 'Art. 28 — Processor' },
    { key: 'ART29', name: 'Art. 29 — Processing under the authority of controller or processor' },
    { key: 'ART30', name: 'Art. 30 — Records of processing activities' },
    { key: 'ART32', name: 'Art. 32 — Security of processing' },
    {
      key: 'ART33',
      name: 'Art. 33 — Notification of a personal data breach to the supervisory authority',
    },
    { key: 'ART34', name: 'Art. 34 — Communication of a personal data breach to the data subject' },
    { key: 'ART35', name: 'Art. 35 — Data protection impact assessment' },
    { key: 'ART37', name: 'Art. 37 — Designation of the data protection officer' },
    { key: 'ART44', name: 'Art. 44 — General principle for transfers' },
    { key: 'ART45', name: 'Art. 45 — Transfers on the basis of an adequacy decision' },
    { key: 'ART46', name: 'Art. 46 — Transfers subject to appropriate safeguards' },
    { key: 'ART47', name: 'Art. 47 — Binding corporate rules' },
    { key: 'ART49', name: 'Art. 49 — Derogations for specific situations' },
  ],
  mappings: [
    // ── Article 5 — Principles relating to processing ─────────────────────
    { externalId: 'GDPR-ART5', scfId: 'SCF-DAT-003', ref: 'Art. 5(1)(e)' }, // storage limitation
    { externalId: 'GDPR-ART5', scfId: 'SCF-DAT-002', ref: 'Art. 5' },
    { externalId: 'GDPR-ART5', scfId: 'SCF-DAT-001', ref: 'Art. 5(1)(f)' }, // integrity/confidentiality
    { externalId: 'GDPR-ART5', scfId: 'SCF-PRV-002', ref: 'Art. 5' }, // purpose limitation
    { primary: true, externalId: 'GDPR-ART5', scfId: 'SCF-PRV-001', ref: 'Art. 5' }, // primary — lawfulness

    // ── Article 6 — Lawfulness of processing ─────────────────────────────
    { externalId: 'GDPR-ART6', scfId: 'SCF-PRV-001', ref: 'Art. 6' },

    // ── Article 7 — Conditions for consent ───────────────────────────────
    { externalId: 'GDPR-ART7', scfId: 'SCF-PRV-008', ref: 'Art. 7' },

    // ── Article 9 — Special categories of personal data ──────────────────
    { externalId: 'GDPR-ART9', scfId: 'SCF-DAT-002', ref: 'Art. 9' },
    { primary: true, externalId: 'GDPR-ART9', scfId: 'SCF-PRV-001', ref: 'Art. 9' }, // TODO(RISK-controlIndex-primary): primary chosen to preserve prior (accidental) last-wins behavior — needs compliance SME confirmation

    // ── Articles 13–14 — Transparency and privacy notices ────────────────
    // Articles 13 and 14 are the privacy notice, not a policy. They also
    // mapped to SCF-GOV-001 (information security policies), which is the
    // same shape as the ISO 27001 clause 7.5.1 defect — the policy control
    // answering a clause that is not about policy. SCF-PRV-009 carries the
    // substance of both articles, and SCF-GOV-001 keeps its GDPR coverage
    // through Article 24, which does require data protection policies
    // (GH #6844).
    { externalId: 'GDPR-ART13', scfId: 'SCF-GOV-009', ref: 'Art. 13' }, // legal register (secondary)
    { primary: true, externalId: 'GDPR-ART13', scfId: 'SCF-PRV-009', ref: 'Art. 13' }, // primary — transparency and privacy notices
    { externalId: 'GDPR-ART14', scfId: 'SCF-GOV-009', ref: 'Art. 14' }, // legal register (secondary)
    { primary: true, externalId: 'GDPR-ART14', scfId: 'SCF-PRV-009', ref: 'Art. 14' }, // primary — transparency and privacy notices

    // ── Articles 15–22 — Data subject rights ─────────────────────────────
    { externalId: 'GDPR-ART15', scfId: 'SCF-PRV-003', ref: 'Art. 15' }, // access
    { externalId: 'GDPR-ART16', scfId: 'SCF-PRV-003', ref: 'Art. 16' }, // rectification
    { externalId: 'GDPR-ART17', scfId: 'SCF-PRV-003', ref: 'Art. 17' }, // erasure
    { externalId: 'GDPR-ART18', scfId: 'SCF-PRV-003', ref: 'Art. 18' }, // restriction
    { externalId: 'GDPR-ART19', scfId: 'SCF-PRV-003', ref: 'Art. 19' }, // notification obligation
    { externalId: 'GDPR-ART20', scfId: 'SCF-PRV-003', ref: 'Art. 20' }, // data subject rights (secondary)
    { primary: true, externalId: 'GDPR-ART20', scfId: 'SCF-DAT-006', ref: 'Art. 20' }, // primary — data portability and transfer
    { externalId: 'GDPR-ART21', scfId: 'SCF-PRV-003', ref: 'Art. 21' }, // objection
    { externalId: 'GDPR-ART22', scfId: 'SCF-PRV-003', ref: 'Art. 22' }, // automated decision-making

    // ── Article 24 — Responsibility of the controller ─────────────────────
    { externalId: 'GDPR-ART24', scfId: 'SCF-MSS-001', ref: 'Art. 24' },
    { externalId: 'GDPR-ART24', scfId: 'SCF-MSS-004', ref: 'Art. 24' },
    { externalId: 'GDPR-ART24', scfId: 'SCF-GOV-002', ref: 'Art. 24' },
    { externalId: 'GDPR-ART24', scfId: 'SCF-GOV-001', ref: 'Art. 24' },
    { primary: true, externalId: 'GDPR-ART24', scfId: 'SCF-GOV-004', ref: 'Art. 24' }, // primary

    // ── Article 25 — Data protection by design and default ────────────────
    { externalId: 'GDPR-ART25', scfId: 'SCF-DEV-003', ref: 'Art. 25' },
    { externalId: 'GDPR-ART25', scfId: 'SCF-DEV-001', ref: 'Art. 25' },
    { primary: true, externalId: 'GDPR-ART25', scfId: 'SCF-PRV-007', ref: 'Art. 25' }, // primary

    // ── Article 28 — Processor requirements (DPAs) ────────────────────────
    { externalId: 'GDPR-ART28', scfId: 'SCF-VDR-004', ref: 'Art. 28' },
    { externalId: 'GDPR-ART28', scfId: 'SCF-VDR-002', ref: 'Art. 28' },
    { primary: true, externalId: 'GDPR-ART28', scfId: 'SCF-VDR-001', ref: 'Art. 28' }, // primary

    // ── Article 29 — Processing under authority ────────────────────────────
    { externalId: 'GDPR-ART29', scfId: 'SCF-PPL-006', ref: 'Art. 29' }, // confidentiality/NDA
    { primary: true, externalId: 'GDPR-ART29', scfId: 'SCF-PPL-002', ref: 'Art. 29' }, // primary — contractual obligation to process only on instruction

    // ── Article 30 — Records of processing activities ─────────────────────
    { externalId: 'GDPR-ART30', scfId: 'SCF-PRV-002', ref: 'Art. 30' },

    // ── Article 32 — Security of processing ───────────────────────────────
    { externalId: 'GDPR-ART32', scfId: 'SCF-NET-005', ref: 'Art. 32' }, // cryptography
    { externalId: 'GDPR-ART32', scfId: 'SCF-IAM-008', ref: 'Art. 32' }, // secure authentication
    { externalId: 'GDPR-ART32', scfId: 'SCF-IAM-001', ref: 'Art. 32' }, // access control
    { externalId: 'GDPR-ART32', scfId: 'SCF-OPS-006', ref: 'Art. 32' }, // security logging
    { externalId: 'GDPR-ART32', scfId: 'SCF-OPS-001', ref: 'Art. 32' }, // endpoint security
    { externalId: 'GDPR-ART32', scfId: 'SCF-DAT-002', ref: 'Art. 32' },
    { primary: true, externalId: 'GDPR-ART32', scfId: 'SCF-DAT-001', ref: 'Art. 32' }, // primary

    // ── Article 33 — Breach notification to supervisory authority (72h) ───
    { externalId: 'GDPR-ART33', scfId: 'SCF-INC-003', ref: 'Art. 33' },
    { externalId: 'GDPR-ART33', scfId: 'SCF-INC-001', ref: 'Art. 33' },
    { primary: true, externalId: 'GDPR-ART33', scfId: 'SCF-INC-006', ref: 'Art. 33' }, // primary

    // ── Article 34 — Communication to data subjects ────────────────────────
    { externalId: 'GDPR-ART34', scfId: 'SCF-INC-003', ref: 'Art. 34' },
    { primary: true, externalId: 'GDPR-ART34', scfId: 'SCF-INC-006', ref: 'Art. 34' }, // primary

    // ── Article 35 — Data Protection Impact Assessment ─────────────────────
    { externalId: 'GDPR-ART35', scfId: 'SCF-PRV-004', ref: 'Art. 35' },

    // ── Article 37 — Designation of DPO ───────────────────────────────────
    { externalId: 'GDPR-ART37', scfId: 'SCF-PRV-005', ref: 'Art. 37' },

    // ── Articles 44–49 — Transfers to third countries ─────────────────────
    { externalId: 'GDPR-ART44', scfId: 'SCF-PRV-006', ref: 'Art. 44' },
    { externalId: 'GDPR-ART45', scfId: 'SCF-PRV-006', ref: 'Art. 45' },
    { externalId: 'GDPR-ART46', scfId: 'SCF-PRV-006', ref: 'Art. 46' },
    { externalId: 'GDPR-ART47', scfId: 'SCF-PRV-006', ref: 'Art. 47' },
    { externalId: 'GDPR-ART49', scfId: 'SCF-PRV-006', ref: 'Art. 49' },
  ],
};

module.exports = {
  FRAMEWORK_GDPR,
};
